import { TravelTimeData, TravelLeg } from '../types';

export interface TravelTimeValidationError {
  key: string;
  params?: Record<string, string>;
}

function isLegEmpty(leg?: TravelLeg): boolean {
  if (!leg) return true;
  return !leg.date && !leg.departureTime && !leg.arrivalTime;
}

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function validateLeg(
  leg: TravelLeg | undefined,
  legKey: 'outbound' | 'inbound',
  errors: TravelTimeValidationError[]
): void {
  if (!leg || isLegEmpty(leg)) return;

  // Rule 1: If any field is set, date + departureTime + arrivalTime must all be present
  if (!leg.date || !leg.departureTime || !leg.arrivalTime) {
    errors.push({ key: 'messages.travelTimeIncomplete', params: { leg: legKey } });
    return;
  }

  const depMinutes = timeToMinutes(leg.departureTime);
  const arrMinutes = timeToMinutes(leg.arrivalTime) + (leg.nextDay ? 24 * 60 : 0);

  // Rule 2: Arrival cannot be before departure (unless +1 is active)
  if (arrMinutes < depMinutes) {
    errors.push({ key: 'messages.travelTimeArrivalBeforeDeparture', params: { leg: legKey } });
    return;
  }

  // Rule 3: Break time cannot be >= travel time
  const travelMinutes = arrMinutes - depMinutes;
  const breakTime = leg.breakTime ?? 0;
  if (travelMinutes > 0 && breakTime >= travelMinutes) {
    errors.push({ key: 'messages.travelTimeBreakExceedsTravel', params: { leg: legKey } });
  }
}

export function validateTravelTimeForClose(
  travelTimeData: TravelTimeData | undefined
): TravelTimeValidationError[] {
  const errors: TravelTimeValidationError[] = [];

  if (!travelTimeData) return errors;

  const { outbound, inbound } = travelTimeData;

  // If both legs are completely empty, skip validation entirely
  if (isLegEmpty(outbound) && isLegEmpty(inbound)) return errors;

  validateLeg(outbound, 'outbound', errors);
  validateLeg(inbound, 'inbound', errors);

  // Rule 4: Inbound date cannot be before outbound date (cross-leg, only when both are filled)
  if (!isLegEmpty(outbound) && !isLegEmpty(inbound) && outbound?.date && inbound?.date) {
    if (inbound.date < outbound.date) {
      errors.push({ key: 'messages.travelTimeInboundBeforeOutbound' });
    }
  }

  return errors;
}
